/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { callApi } from 'src/utils/apiCall';
import { useSelector, dispatch } from '../redux/store';
import { getSettingsTemplateData } from '../redux/slices/settingsTemplate';
import { getTemplateList } from '../redux/slices/practiceMessage';
import { sendEngagementEmail } from '../redux/api/practiceMessageApis';
import useCustomSnackbar from './useCustomSnackbar';

/**
 * Maximum number of leads allowed per bulk-send batch.
 * Exported so picker UIs can use it for disable/warn states.
 */
export const MAX_BULK_GOOGLE_REVIEW = 100;

/**
 * Encapsulates the Google Review send + preview flow so that both TaskSidebar
 * and ManageBusiness consume identical logic without duplication.
 *
 * @returns {{
 *   templateData: object|null,
 *   hasTemplate: boolean,
 *   isSending: boolean,
 *   previewOpen: boolean,
 *   openPreview: function,
 *   closePreview: function,
 *   sendRequest: function,
 *   sendRequestBulk: function,
 * }}
 */
export default function useGoogleReviewSend() {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const settingsTemplatePrefs = useSelector((state) => state.settingsTemplate.templateData);
    const practiceMessageTemplates = useSelector((state) => state.practiceMessage.templateDetails);
    const { enqueueSnackbar } = useCustomSnackbar();

    const [isSending, setIsSending] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    // Ensure template prefs + details are loaded when the hook mounts
    useEffect(() => {
        if (practiceId) {
            if (!settingsTemplatePrefs) dispatch(getSettingsTemplateData(practiceId));
            if (!practiceMessageTemplates?.results?.length) dispatch(getTemplateList(practiceId, true));
        }
    }, [practiceId]);

    // Resolve the GOOGLE_REVIEW_LINK preference → full template detail
    const googleReviewPref = settingsTemplatePrefs?.results?.find(
        (t) => t.type === 'GOOGLE_REVIEW_LINK'
    );
    const templateData = googleReviewPref?.template
        ? (practiceMessageTemplates?.results?.find((t) => t.id === googleReviewPref.template) ?? null)
        : null;
    const hasTemplate = Boolean(templateData);

    /** Open the preview dialog (or warn if no template configured). */
    const openPreview = (e) => {
        e?.stopPropagation?.();
        if (templateData) {
            setPreviewOpen(true);
        } else {
            enqueueSnackbar('No Google review template configured.', { variant: 'warning' });
        }
    };

    const closePreview = () => setPreviewOpen(false);

    /**
     * Send a Google review request email to a single lead.
     * Used by TaskSidebar's CLOSED-lead quick-action flow.
     *
     * Payload: only `lead` + `lead_treatment` — per PracticeEngagementSendSerializer.
     * Optional `subject` / `body` overrides are forwarded when provided (per-send edits
     * from the compose step in GoogleReviewSendDialog).
     * `category` / `channel` are template fields, not accepted on the send endpoint.
     *
     * @param {{ leadId: number, leadTreatmentId: number, leadData: object, subject?: string, body?: string }} context
     * @param {{ onSuccess?: function }} options
     */
    const sendRequest = ({ leadId, leadTreatmentId, leadData, subject, body } = {}, { onSuccess } = {}) => {
        if (!googleReviewPref?.template) {
            enqueueSnackbar(
                'No Google review template configured. Open Settings → Message Templates (/dashboard/user/message) and set a default for "Google review link".',
                { variant: 'warning' }
            );
            return;
        }
        setIsSending(true);
        // Only send fields accepted by PracticeEngagementSendSerializer
        const detail = {
            lead: leadId,
            lead_treatment: leadTreatmentId,
            ...(subject !== undefined && subject !== '' ? { subject } : {}),
            ...(body !== undefined && body !== '' ? { body } : {}),
        };
        const callback = (data) => {
            setTimeout(() => {
                setIsSending(false);
                // Generic text only — no PII; notistack strings are captured by Sentry breadcrumbs
                enqueueSnackbar('Google review request sent successfully', { variant: 'success' });
                if (onSuccess) onSuccess(data);
            }, 2000);
        };
        dispatch(sendEngagementEmail({
            id: googleReviewPref.template,
            detail,
            callback,
            data: leadData,
            errorCallback: () => setIsSending(false),
        }));
    };

    /**
     * Send Google review request emails to multiple leads in parallel.
     * All leads are attempted via Promise.allSettled — does NOT stop on first error.
     * Emits a single summary snackbar (no PII — names stay in returned outcomes only).
     * Hard-capped at MAX_BULK_GOOGLE_REVIEW leads per call.
     *
     * `overrides.subject` / `overrides.body` are forwarded on every send when provided
     * (per-send edits from the compose step in GoogleReviewSendDialog).
     *
     * @param {Array<{ leadId, leadTreatmentId, leadData }>} entries
     * @param {{ onProgress?: function, onSuccess?: function({ sent, failed }), overrides?: { subject?: string, body?: string } }} options
     */
    const sendRequestBulk = async (entries, { onProgress, onSuccess, overrides } = {}) => {
        if (!googleReviewPref?.template) {
            enqueueSnackbar(
                'No Google review template configured. Open Settings → Message Templates (/dashboard/user/message) and set a default for "Google review link".',
                { variant: 'warning' }
            );
            return;
        }
        if (entries.length > MAX_BULK_GOOGLE_REVIEW) {
            enqueueSnackbar(
                `Bulk send is limited to ${MAX_BULK_GOOGLE_REVIEW} patients at a time. Please reduce your selection.`,
                { variant: 'warning' }
            );
            return;
        }
        setIsSending(true);
        const templateId = googleReviewPref.template;

        // Build the per-send overrides once — only include defined non-empty values
        const sendOverrides = {};
        if (overrides?.subject !== undefined && overrides.subject !== '') sendOverrides.subject = overrides.subject;
        if (overrides?.body !== undefined && overrides.body !== '') sendOverrides.body = overrides.body;

        // Run all sends in parallel (callApi's internal pool caps concurrency at 10)
        // Progress fires per-request via .finally() so the bar advances in real time
        let done = 0;
        const settled = await Promise.allSettled(
            entries.map(({ leadId, leadTreatmentId }) =>
                callApi(
                    `/api/v1/practices/${practiceId}/engagement-templates/${templateId}/send/`,
                    'post',
                    // Only fields accepted by PracticeEngagementSendSerializer
                    { lead: leadId, lead_treatment: leadTreatmentId, ...sendOverrides }
                ).finally(() => {
                    done += 1;
                    if (onProgress) onProgress(done, entries.length);
                })
            )
        );

        const sentLeads = [];
        const failedLeads = [];

        settled.forEach((result, i) => {
            const { leadData } = entries[i];
            if (result.status === 'fulfilled' && result.value?.ok) {
                sentLeads.push(leadData);
            } else {
                failedLeads.push({ leadData, error: result.reason?.message || 'Request failed' });
            }
        });

        setIsSending(false);

        const total = entries.length;
        const sentCount = sentLeads.length;
        const failCount = failedLeads.length;

        // One summary snackbar — generic text only, no PII dispatched to Redux/Sentry
        if (failCount === 0) {
            enqueueSnackbar(
                `Review request${sentCount !== 1 ? 's' : ''} sent to ${sentCount} patient${sentCount !== 1 ? 's' : ''}.`,
                { variant: 'success' }
            );
        } else {
            enqueueSnackbar(
                `Sent to ${sentCount} of ${total} patients. ${failCount} failed — see results.`,
                { variant: failCount === total ? 'error' : 'warning' }
            );
        }

        // Outcomes carry leadData objects so dialog can render names locally
        if (onSuccess) onSuccess({ sent: sentLeads, failed: failedLeads });
    };

    return {
        templateData,
        hasTemplate,
        isSending,
        previewOpen,
        openPreview,
        closePreview,
        sendRequest,
        sendRequestBulk,
    };
}
